import { dayjs, ElTableColumn } from 'element-plus'
import { useI18n } from 'i18n-utils'
import { ApiStatus, ContractServices, OrderDirection } from 'network'
import { useBaseInfoStore, useDoubleContractKlineStore, useDoubleContractOrderListStore, useExchangeInfoStore, useProjectTheme, useResponsive } from 'store'
import { ContractTypeIces } from 'store/src/constants/common'
import { ControlledAmount, CustomButton, EmptyData, FullScreenMinHeight, HocElTable, ListCell, Tag, UiUtils } from 'ui-components'
import '../styles/order-list-layout.scss'
import '../styles/position-item-layout.scss'

export const StrategyOrders = defineComponent({
  setup() {
    const { t } = useI18n()
    const themeStore = useProjectTheme()
    const theme = computed(() => themeStore.theme)
    const responsive = useResponsive()
    const baseInfoStore = useBaseInfoStore()
    const exchangeStore = useExchangeInfoStore()
    const klineStore = useDoubleContractKlineStore()
    const orderListStore = useDoubleContractOrderListStore()

    const orders = computed(() => {
      return orderListStore.icebergStrategyOrders.filter((item) => {
        if (orderListStore.strategyHideOtherOrder) {
          return item.symbolId === klineStore.currentSymbolId
        }
        return true
      })
    })

    const handleStopStrategy = (data: ApiResponse.DoubleContractIcebergStrategyOrder) => {
      UiUtils.showModal(t('是否确认停止此策略？'), {
        onConfirm: async () => {
          const res = await ContractServices.doubleContractStopStrategy({ strategyId: data.strategyId })
          if (res.status === ApiStatus.Success) {
            orderListStore.refreshIcebergStrategyOrders()
            UiUtils.showSuccess(t('停止成功'))
          }
        },
      })
    }

    const handleStartOrder = (data: ApiResponse.DoubleContractIcebergStrategyOrder) => {
      UiUtils.showModal(t('是否确认立即此开始策略？'), {
        onConfirm: async () => {
          const res = await ContractServices.doubleContractStartOrder({ strategyId: data.strategyId })
          if (res.status === ApiStatus.Success) {
            orderListStore.refreshIcebergStrategyOrders()
            UiUtils.showSuccess(t('开始成功'))
          }
        },
      })
    }

    return () => {
      if (responsive.isMobile) {
        if (orders.value.length === 0) {
          return (
            <div>
              <EmptyData />
            </div>
          )
        }
        return orders.value.map((item) => {
          const orderType = orderListStore.getIcebergStrategyOrderType(item)

          return (
            <div class="position-item-layout position-item divider">
              <div class="symbol-coin-info flex justify-between !mb-[8px]">
                <div class="flex flex-row items-center">
                  <img width={32} height={32} src={baseInfoStore.getContractCoinIconUrlBySymbolId(item.symbolId)} alt="coin icon" />
                  <div class="coin-info">
                    <div class="coin-name">
                      {exchangeStore.getContractSymbolName(item.symbolId)} {t('永续')}
                    </div>
                    <div class="flex info-tags gap-4">
                      {orderType && (
                        <Tag class="!text-[10px]" color={item.direction === OrderDirection.Buy ? theme.value.colors.up : theme.value.colors.down}>
                          {t(orderType)}
                        </Tag>
                      )}
                      <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base} class="!text-[10px]">
                        {t('冰山委托')}
                      </Tag>
                      <Tag class="!text-[10px]" color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                        {item.marginType === 1 ? t('逐仓') : t('全仓')} {item.leverage}X
                      </Tag>
                    </div>
                  </div>
                </div>
                <Tag class="!text-[12px]" color={orderListStore.getStrategyStatus(item).orderStatusColor}>
                  {orderListStore.getStrategyStatus(item).orderStatus}
                </Tag>
              </div>

              {/* 委托总量 */}
              <ListCell
                title={t('委托总量')}
                description={
                  <ControlledAmount>
                    {orderListStore.getIcebergStrategyTotalQty(item).formatCommonMoney()}&nbsp;
                    {exchangeStore.getFromSymbolNameBySymbolId(item.symbolId)}
                  </ControlledAmount>
                }
              />

              {/* 已成交量 */}
              <ListCell
                title={t('已成交量')}
                description={
                  <ControlledAmount>
                    {orderListStore.getIcebergStrategyAlreadyQty(item).formatCommonMoney()}&nbsp;
                    {exchangeStore.getFromSymbolNameBySymbolId(item.symbolId)}
                  </ControlledAmount>
                }
              />

              {/* 单笔委托量 */}
              {![3, 4].includes(item.status) && (
                <ListCell
                  title={t('单笔委托量')}
                  description={
                    <ControlledAmount>
                      {orderListStore.getIcebergStrategySingleQty(item).formatCommonMoney()}&nbsp;
                      {exchangeStore.getFromSymbolNameBySymbolId(item.symbolId)}
                    </ControlledAmount>
                  }
                />
              )}

              {/* 挂单笔数 */}
              <ListCell title={t('挂单笔数')} description={<ControlledAmount>{item.orderNum}</ControlledAmount>} />

              {/* 挂单偏好 */}
              <ListCell title={t('挂单偏好')} description={<ControlledAmount>{t(ContractTypeIces[item.preferenceType - 1])}</ControlledAmount>} />

              {/* 触发条件 */}
              <ListCell title={t('触发条件')} description={<ControlledAmount>{orderListStore.getIcebergStrategyTriggerCondition(item)}</ControlledAmount>} />

              {/* 创建时间 */}
              <ListCell title={t('创建时间')} description={dayjs(item.createTime).format('YYYY-MM-DD HH:mm:ss')} />

              {/* 完成时间 */}
              {item.status === 3 && <ListCell title={t('完成时间')} description={dayjs(item.updateTime).format('YYYY-MM-DD HH:mm:ss')} />}

              {/* 触发时间 */}
              {item.status === 4 && <ListCell title={t('触发时间')} description={dayjs(item.updateTime).format('YYYY-MM-DD HH:mm:ss')} />}

              {/* 失败原因 */}
              {item.status === 4 && <ListCell title={t('失败原因')} description={item.reason} />}

              {/* 操作 */}
              <div class="flex items-center gap-3">
                {item.status === 1 && (
                  <CustomButton class="flex-1 !bg-[var(--colors-bg-tag-base)] !border-none" onClick={() => handleStartOrder(item)}>
                    {t('立即开始')}
                  </CustomButton>
                )}

                <CustomButton class={`${item.status === 1 ? 'flex-1' : 'w-full'} !bg-[var(--colors-bg-tag-base)] !border-none`} onClick={() => handleStopStrategy(item)}>
                  {t('停止')}
                </CustomButton>
              </div>
            </div>
          )
        })
      }

      return (
        <div class="contract-trade-order-list-layout">
          <HocElTable data={orders.value}>
            {{
              default: () => (
                <>
                  <ElTableColumn prop="symbolId" label={t('合约')} minWidth={140}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractIcebergStrategyOrder }) => {
                        const orderType = orderListStore.getIcebergStrategyOrderType(row)

                        return (
                          <div class={'flex align-center gap-row-8 align-content-start'}>
                            <img width={32} src={baseInfoStore.getContractCoinIconUrlBySymbolId(row.symbolId)} alt="" />
                            <div class={'symbol-info'}>
                              <span class="symbol-name">
                                {exchangeStore.getContractSymbolName(row.symbolId)} {t('永续')}
                              </span>
                              <div class="flex info-tags gap-4">
                                {orderType && <Tag color={row.direction === OrderDirection.Buy ? theme.value.colors.up : theme.value.colors.down}>{t(orderType)}</Tag>}
                                <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                                  {t('冰山委托')}
                                </Tag>
                              </div>
                            </div>
                          </div>
                        )
                      },
                    }}
                  </ElTableColumn>

                  <ElTableColumn prop="marginType" label={t('保证金模式')} minWidth={100}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractPosition }) => {
                        return (
                          <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                            {row.marginType === 1 ? t('逐仓') : t('全仓')}
                            {row.leverage}X
                          </Tag>
                        )
                      },
                    }}
                  </ElTableColumn>

                  <ElTableColumn prop="status" label={t('状态')}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractIcebergStrategyOrder }) => {
                        return <Tag color={orderListStore.getStrategyStatus(row).orderStatusColor}>{orderListStore.getStrategyStatus(row).orderStatus}</Tag>
                      },
                    }}
                  </ElTableColumn>

                  <ElTableColumn prop="totalQty" label={t('委托总量')}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractIcebergStrategyOrder }) => {
                        return (
                          <ControlledAmount>
                            {orderListStore.getIcebergStrategyTotalQty(row).formatCommonMoney()}
                            &nbsp;
                            {exchangeStore.getFromSymbolNameBySymbolId(row.symbolId)}
                          </ControlledAmount>
                        )
                      },
                    }}
                  </ElTableColumn>

                  <ElTableColumn prop="alreadyQty" label={t('已成交量')}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractIcebergStrategyOrder }) => {
                        return (
                          <ControlledAmount>
                            {orderListStore.getIcebergStrategyAlreadyQty(row).formatCommonMoney()}
                            &nbsp;
                            {exchangeStore.getFromSymbolNameBySymbolId(row.symbolId)}
                          </ControlledAmount>
                        )
                      },
                    }}
                  </ElTableColumn>

                  <ElTableColumn prop="singleQty" label={t('单笔委托量')} minWidth={100}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractIcebergStrategyOrder }) => {
                        if (row.status === 3 || row.status === 4) return '--'
                        return (
                          <ControlledAmount>
                            {orderListStore.getIcebergStrategySingleQty(row).formatCommonMoney()}
                            &nbsp;
                            {exchangeStore.getFromSymbolNameBySymbolId(row.symbolId)}
                          </ControlledAmount>
                        )
                      },
                    }}
                  </ElTableColumn>

                  <ElTableColumn prop="orderNum" label={t('挂单笔数')}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractIcebergStrategyOrder }) => {
                        return <ControlledAmount>{row.orderNum}</ControlledAmount>
                      },
                    }}
                  </ElTableColumn>

                  <ElTableColumn prop="orderNum" label={t('挂单偏好')}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractIcebergStrategyOrder }) => {
                        return <ControlledAmount>{t(ContractTypeIces[row.preferenceType - 1])}</ControlledAmount>
                      },
                    }}
                  </ElTableColumn>

                  <ElTableColumn prop="orderNum" label={t('触发条件')}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractIcebergStrategyOrder }) => {
                        return <ControlledAmount>{orderListStore.getIcebergStrategyTriggerCondition(row)}</ControlledAmount>
                      },
                    }}
                  </ElTableColumn>

                  <ElTableColumn prop="createTime" label={t('创建时间')} width={160}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractIcebergStrategyOrder }) => {
                        return dayjs(row.createTime).format('YYYY-MM-DD HH:mm:ss')
                      },
                    }}
                  </ElTableColumn>

                  <ElTableColumn prop="createTime" label={t('完成时间')}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractIcebergStrategyOrder }) => {
                        if (row.status !== 3) return '--'
                        return dayjs(row.updateTime).format('YYYY-MM-DD HH:mm:ss')
                      },
                    }}
                  </ElTableColumn>

                  <ElTableColumn prop="createTime" label={t('触发时间')}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractIcebergStrategyOrder }) => {
                        if (row.status !== 4) return '--'
                        return dayjs(row.updateTime).format('YYYY-MM-DD HH:mm:ss')
                      },
                    }}
                  </ElTableColumn>

                  <ElTableColumn prop="createTime" label={t('失败原因')}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractIcebergStrategyOrder }) => {
                        if (row.status !== 4) return '--'
                        return row.reason
                      },
                    }}
                  </ElTableColumn>

                  <ElTableColumn prop="createTime" label={t('操作')} width={160}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractIcebergStrategyOrder }) => {
                        return (
                          <div class="action-btns">
                            {row.status === 1 && <span onClick={() => handleStartOrder(row)}>{t('立即开始')}</span>}
                            <span onClick={() => handleStopStrategy(row)}>{t('停止')}</span>
                          </div>
                        )
                      },
                    }}
                  </ElTableColumn>
                </>
              ),
              empty: () => {
                return (
                  <FullScreenMinHeight>
                    <EmptyData />
                  </FullScreenMinHeight>
                )
              },
            }}
          </HocElTable>
        </div>
      )
    }
  },
})
